/**
 * FooterSection Component
 * Handles mobile accordion functionality for footer sections
 * Only active on mobile screens (< md breakpoint)
 */
export function FooterSection(element) {
    const titles = element.querySelectorAll(".js_footer_section_title");
    const MOBILE_BREAKPOINT = parseInt(element.getAttribute("data-breakpoint") ?? "768", 10); // md breakpoint in Tailwind
    const mobileMediaQuery = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    /**
     * Check if current viewport is mobile
     */
    const isMobile = () => mobileMediaQuery.matches;
    /**
     * Update accessibility attributes for links within content
     */
    const updateContentAccessibility = (content, isHidden) => {
        const links = content.querySelectorAll("a");
        if (isHidden) {
            // Hide from screen readers and remove from tab order
            content.setAttribute("aria-hidden", "true");
            links.forEach((link) => {
                link.setAttribute("tabindex", "-1");
            });
        }
        else {
            // Make accessible again
            content.removeAttribute("aria-hidden");
            links.forEach((link) => {
                link.removeAttribute("tabindex");
            });
        }
    };
    /**
     * Toggle accordion section
     */
    const toggleSection = (title) => {
        if (!isMobile())
            return;
        const content = element.querySelector(".js_footer_section_content");
        const plusIcon = title.querySelector(".js_footer_icon_plus");
        const minusIcon = title.querySelector(".js_footer_icon_minus");
        if (!content)
            return;
        const isExpanded = content.getAttribute("data-expanded") === "true";
        if (isExpanded) {
            // Collapse
            content.style.maxHeight = "0";
            content.style.opacity = "0";
            content.style.marginBottom = "0";
            content.setAttribute("data-expanded", "false");
            title.setAttribute("aria-expanded", "false");
            updateContentAccessibility(content, true);
            if (plusIcon && minusIcon) {
                plusIcon.classList.remove("hidden");
                minusIcon.classList.add("hidden");
            }
        }
        else {
            // Expand
            content.style.maxHeight = content.scrollHeight + "px";
            content.style.opacity = "1";
            content.style.marginBottom = "16px";
            content.setAttribute("data-expanded", "true");
            title.setAttribute("aria-expanded", "true");
            updateContentAccessibility(content, false);
            if (plusIcon && minusIcon) {
                plusIcon.classList.add("hidden");
                minusIcon.classList.remove("hidden");
            }
        }
    };
    /**
     * Initialize accordion state based on viewport
     */
    const initializeState = () => {
        titles.forEach((title) => {
            const content = element.querySelector(".js_footer_section_content");
            const plusIcon = title.querySelector(".js_footer_icon_plus");
            const minusIcon = title.querySelector(".js_footer_icon_minus");
            if (!content)
                return;
            if (isMobile()) {
                // Mobile: collapsed by default
                content.style.transition = "all 0.3s ease";
                content.style.overflow = "hidden";
                content.style.maxHeight = "0";
                content.style.opacity = "0";
                content.setAttribute("data-expanded", "false");
                // Set ARIA attributes
                title.setAttribute("aria-expanded", "false");
                title.setAttribute("tabindex", "0");
                // Hide content from accessibility when collapsed
                updateContentAccessibility(content, true);
                // Show plus icon, hide minus icon
                if (plusIcon && minusIcon) {
                    plusIcon.classList.remove("hidden");
                    minusIcon.classList.add("hidden");
                }
                // Make title clickable
                title.style.cursor = "pointer";
                title.style.userSelect = "none";
            }
            else {
                // Desktop: always expanded, remove inline styles
                content.style.maxHeight = "";
                content.style.opacity = "";
                content.style.overflow = "";
                content.style.transition = "";
                content.style.marginBottom = "";
                content.removeAttribute("data-expanded");
                // Remove mobile-specific ARIA attributes on desktop
                title.setAttribute("aria-expanded", "true");
                title.removeAttribute("tabindex");
                // Make content accessible on desktop
                updateContentAccessibility(content, false);
                title.style.cursor = "";
                title.style.userSelect = "";
            }
        });
    };
    /**
     * Handle click on title
     */
    const handleClick = (event) => {
        const title = event.currentTarget;
        toggleSection(title);
    };
    /**
     * Handle keyboard events (Enter and Space)
     */
    const handleKeyDown = (event) => {
        // Only handle Enter and Space keys
        if (event.key !== "Enter" && event.key !== " ")
            return;
        // Prevent default scrolling behavior for Space key
        event.preventDefault();
        const title = event.currentTarget;
        toggleSection(title);
    };
    /**
     * Re-initialize only when crossing the mobile/desktop breakpoint.
     * Avoid window "resize" — mobile browsers fire it when the address bar
     * shows/hides during scroll, which was collapsing open accordions.
     */
    const handleBreakpointChange = () => {
        initializeState();
    };
    // Initialize
    initializeState();
    // Add click listeners
    titles.forEach((title) => {
        title.addEventListener("click", handleClick);
        title.addEventListener("keydown", handleKeyDown);
    });
    mobileMediaQuery.addEventListener("change", handleBreakpointChange);
    // Cleanup function
    return () => {
        titles.forEach((title) => {
            title.removeEventListener("click", handleClick);
            title.removeEventListener("keydown", handleKeyDown);
            title.style.cursor = "";
            title.style.userSelect = "";
        });
        mobileMediaQuery.removeEventListener("change", handleBreakpointChange);
    };
}
